import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';

import { withDevtools } from '@ngrx-toolkit/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, of, pipe, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { FinanceType } from '@ph-core/enums/finance-type.enum';
import { Options } from '@ph-core/enums/options.enum';
import { VehicleConditionType } from '@ph-core/enums/vehicle-condition.enum';
import { VehicleType } from '@ph-core/enums/vehicle-type.enum';
import { ConfigService } from '@ph-core/services/config/config.service';
import { withConditionalStorageSync } from '@ph-core/utils/with-conditional-storage-sync';
import { CarfaxResponse } from '@ph-model/api/response/carfax-response.model';
import { DecodeVinResponse } from '@ph-model/api/response/decode-vin.response';
import { ManualResponse } from '@ph-model/api/response/manual.response';
import { PostalResponse } from '@ph-model/api/response/postal.response.model';
import { FinanceForm } from '@ph-model/form/finance-form';
import { IComplexDate } from '@ph-shared/utils/date-control-formater';
import { LoadQuote } from '@ph-shopping/model/cart';
import { ShoppingFlowType } from '@ph-shopping/model/shopping-flow-type.enum';
import { UserStore } from '@ph-store/user/user.store';

import { VehicleStoreRequestsDispatcher } from './vehicle-store-requests.dispatcher';

const storeKey = 'VehicleStore';

export interface VehicleState {
  dotForm?: DOTForm;
  dotForm1?: DOTForm;
  dotForm2?: DOTForm;
  selectedSurcharges: { name: string; selected: boolean }[];
  values: VehicleValues;
  vin: DecodeVinResponse;
  postal: PostalResponse;
  error: string;
  status: boolean;
  financeForm: FinanceForm;
  borrowInfoForm: BorrowInfoForm;
  decodeVinTries: number;
  manualDecode: ManualResponse;
  disableInServiceDate: boolean;
  disableVehicleCondition: boolean;
  chargerForm: ChargerForm;
  dotInfo: { dotForm: DOTForm[] };
  chargerFormEnabled: boolean;
  vehicleFormEnabled: boolean;
  isManualDecodeModeEnabled: boolean;
}

export interface DOTForm {
  rimSize?: string;
  quantity?: number;
  dot?: string[];
}

export interface VehicleValues {
  vin?: string | null;
  hin?: string | null;
  pid?: string | null;
  tin?: string | null;
  vehicleType: VehicleType;
  year: string | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  other: string;
  odometer: string;
  isAfterSale: Options;
  financeType: FinanceType;
  vehicleCondition: VehicleConditionType;
  surcharges: { name: string; selected?: boolean }[];
  msrp: string;
  grossCapCost: string;
  financeAmount: number;
  financeTerm: number;
  loanTerm: number;
  apr: number;
  purchasePrice: string;
  amortizationPeriod: number;
  residualValue: string;
  coBorrower: Options;
  province: string;
  provinceCoBorrower: string;
  DOBBorrower: IComplexDate;
  DOBCoBorrower: IComplexDate;
  flowType: ShoppingFlowType;
  addInsurance: Options;
  customerTaxExempt: Options;
  negativeEquity: Options;
  registrationNumber: string;
  saleDate: IComplexDate;
  inServiceDate?: IComplexDate | null;
  purchaseDate: IComplexDate;
  monthlyPayment: string | null;
  isCreditAvailable?: boolean;
  isValidSaveCredit?: boolean;
  isOldVehicleCondition?: boolean;
}

export interface ChargerForm {
  serialNumber: string;
  chargerYear: string;
  chargerModel: string;
  chargerPurchaseDate: IComplexDate | null;
  chargerPurchasePrice: string;
}

const initialChargerForm: ChargerForm = {
  serialNumber: '',
  chargerYear: '',
  chargerModel: '',
  chargerPurchaseDate: null,
  chargerPurchasePrice: '',
};

const initialDOTForm: DOTForm = {
  rimSize: '',
  quantity: undefined,
  dot: [],
};

export interface BorrowInfoForm {
  DOBBorrower: Date | IComplexDate | null;
  DOBCoBorrower: Date | IComplexDate | null;
  coBorrower: Options;
  provinceBorrower: string | null;
  provinceCoBorrower: string | null;
}

const InitialFinanceFormState: FinanceForm = {
  financeType: null,
  financeAmount: null,
  apr: null,
  loanTerm: null,
  amortizationPeriod: null,
  monthlyPayment: null,
  residualValue: null,
};

const InitialBorrowInfoFormState: BorrowInfoForm = {
  DOBBorrower: null,
  DOBCoBorrower: null,
  coBorrower: Options.NO,
  provinceBorrower: null,
  provinceCoBorrower: null,
};

const InitialState: VehicleState = {
  selectedSurcharges: [],
  values: {} as VehicleValues,
  postal: new PostalResponse(),
  vin: new DecodeVinResponse(),
  error: '',
  status: false,
  financeForm: { ...InitialFinanceFormState },
  borrowInfoForm: { ...InitialBorrowInfoFormState },
  decodeVinTries: 0,
  manualDecode: new ManualResponse(),
  disableInServiceDate: false,
  disableVehicleCondition: false,
  chargerForm: initialChargerForm,
  dotForm: initialDOTForm,
  dotInfo: {
    dotForm: [initialDOTForm],
  },
  dotForm1: initialDOTForm,
  dotForm2: initialDOTForm,
  chargerFormEnabled: false,
  vehicleFormEnabled: true,
  isManualDecodeModeEnabled: false,
};

export type VehicleStoreType = InstanceType<typeof VehicleStore>;

const getParams = (year: string | null, make: string | null, model: string | null, vehicleType: string | null, programId: string): HttpParams => {
  let params = new HttpParams()
    .set('programId', programId)
    .set('year', year ?? 'null')
    .set('make', make ?? 'null')
    .set('model', model ?? 'null')

  if (vehicleType) {
    params = params.set('vehicleType', vehicleType);
  }

  return params;
}

const MANUAL_DECODE_NO_RECORDS_STATUS = 330;

const isEmptyTrim = (trim?: Array<string | null>): boolean =>
  !trim?.length || (trim.length === 1 && !trim[0]);

const hasDecodedVehicleWithoutTrim = (vin: DecodeVinResponse): boolean =>
  !!vin.status && !!vin.year?.[0] && !!vin.make?.[0] && !!vin.model?.[0] && isEmptyTrim(vin.trim);

const toStatusNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

/** Live /manualdecode 330 is often HTTP 330 with an empty body and statusText "No records found". */
const isNoRecordsFoundError = (error: unknown): boolean => {
  const httpError = error as HttpErrorResponse & { error?: { status?: unknown; code?: unknown } };
  const body = httpError.error;
  const bodyRecord = body && typeof body === 'object' ? (body as { status?: unknown; code?: unknown }) : undefined;

  return (
    toStatusNumber(httpError.status) === MANUAL_DECODE_NO_RECORDS_STATUS ||
    httpError.statusText?.toLowerCase() === 'no records found' ||
    toStatusNumber(bodyRecord?.status) === MANUAL_DECODE_NO_RECORDS_STATUS ||
    toStatusNumber(bodyRecord?.code) === MANUAL_DECODE_NO_RECORDS_STATUS
  );
};

export const VehicleStore = signalStore(
  { providedIn: 'root' },
  withDevtools(storeKey),
  withConditionalStorageSync({ key: storeKey }),
  withState<VehicleState>(InitialState),
  withMethods((store, httpClient: HttpClient = inject(HttpClient), userStore = inject(UserStore)) => {
    const environment = ConfigService.getEnvironment();

    const decodeVinManually = rxMethod<void>(
      pipe(
        switchMap(() => {
          const environment = ConfigService.getEnvironment();
          const vehicleType = environment.features.shopping.defaultVehicleType;
          const programId = localStorage.getItem('programId') || '';

          if (!environment.features.shopping.supportManualVinDecoding) {
            return EMPTY;
          }

          return httpClient
            .get<ManualResponse>(`${environment.apiUrl}/manualdecode`, {
              params: getParams(null, null, null, vehicleType, programId),
            })
            .pipe(
              tapResponse<ManualResponse, { error: string }>({
                next: (response: ManualResponse) => {
                  patchState(store, { manualDecode: { year: response.year }, error: '' });
                },
                error: ({ error }) => {
                  console.error(error);
                  const vinReset = new DecodeVinResponse();
                  vinReset.vin = store.vin().vin;

                  patchState(store, {
                    error,
                    vin: vinReset,
                    decodeVinTries: store.decodeVinTries() + 1,
                  });
                },
              })
            );
        })
      )
    );

    const decodeVin = rxMethod<string>(
      pipe(
        switchMap((vin) => {
          const path = environment.features.shopping.supportClassicVinDecodeApi
            ? `${environment.apiUrl}/vindecode`
            : `${environment.apiUrl}/vindecode/v2`;

          const cmsDealerNumber = userStore.user()?.cmsDealerNumber ?? '';

          return httpClient.get<DecodeVinResponse>(path, { params: { vin, dealer: cmsDealerNumber } }).pipe(
            catchError(() => {
              decodeVinManually();
              patchState(store, { decodeVinTries: store.decodeVinTries() + 1 });

              return of(null);
            }),
            filter(Boolean),
            switchMap((decodeVinResponse: DecodeVinResponse) => {
              if (environment.features.shopping.supportCarfaxDecoding && !decodeVinResponse.inserviceDate) {
                return httpClient
                  .get<{
                    standardResponse: { statusCode: number; responseData: CarfaxResponse };
                  }>(`${environment.apiUrl}/carfax`, { params: { vin } })
                  .pipe(
                    map(
                      ({
                        standardResponse: { statusCode, responseData: result },
                      }: {
                        standardResponse: { statusCode: number; responseData: CarfaxResponse };
                      }) => (statusCode === 200 ? result : null)
                    ),
                    map((carfaxResponse: CarfaxResponse | null) => {
                      if (carfaxResponse) {
                        decodeVinResponse.inserviceDate = carfaxResponse.inServiceDate;
                        decodeVinResponse.inserviceDateFound = !!carfaxResponse.inServiceDate;
                      }

                      return decodeVinResponse;
                    }),
                    catchError(() => {
                      return of(decodeVinResponse);
                    }),
                  );
              } else {
                return of(decodeVinResponse);
              }
            }),
            tapResponse<DecodeVinResponse, { error: string }>({
              next: (decodeVinResponse: DecodeVinResponse) => {
                patchState(store, {
                  vin: decodeVinResponse,
                  decodeVinTries: 0,
                  disableInServiceDate: !!decodeVinResponse.inserviceDate,
                  disableVehicleCondition:
                    (decodeVinResponse.vehicleCondition as VehicleConditionType) === VehicleConditionType.CPO,
                  error: '',
                });
              },
              error: ({ error }) => {
                console.error(error);

                patchState(store, {
                  decodeVinTries: store.decodeVinTries() + 1,
                });
              },
            })
          );
        })
      )
    );

    const getYear = rxMethod<string | null>(
      pipe(
        switchMap((category) => {
          const vehicleType = environment.features.shopping.defaultVehicleType || category;
          const programId = localStorage.getItem('programId') || '';

          return httpClient
            .get<ManualResponse>(`${environment.apiUrl}/manualdecode`, {
              params: getParams(null, null, null, vehicleType, programId),
            })
            .pipe(
              tapResponse<ManualResponse, { error: string }>({
                next: (response: ManualResponse) => {
                  patchState(store, {
                    manualDecode: { ...store.manualDecode(), year: response.year },
                    error: '',
                  });
                },
                error: ({ error }) => {
                  console.error(error);
                  const vinReset = new DecodeVinResponse();
                  vinReset.vin = store.vin().vin;

                  patchState(store, {
                    error,
                    vin: vinReset,
                    decodeVinTries: store.decodeVinTries() + 1,
                  });
                },
              })
            );
        })
      )
    );

    const getMake = rxMethod<{ category: string; year: string }>(
      pipe(
        switchMap(({ category, year }) => {
          const vehicleType = environment.features.shopping.defaultVehicleType || category;
          const programId = localStorage.getItem('programId') || '';

          return httpClient
            .get<ManualResponse>(`${environment.apiUrl}/manualdecode`, {
              params: getParams(year, null, null, vehicleType, programId),
            })
            .pipe(
              tapResponse<ManualResponse, { error: string }>({
                next: ({ make }: ManualResponse) => {
                  patchState(store, {
                    manualDecode: { ...store.manualDecode(), make },
                    error: '',
                  });
                },
                error: ({ error }) => {
                  console.error(error);
                  const vinReset = new DecodeVinResponse();
                  vinReset.vin = store.vin().vin;

                  patchState(store, {
                    error,
                    vin: vinReset,
                    decodeVinTries: store.decodeVinTries() + 1,
                  });
                },
              })
            );
        })
      )
    );

    const getModel = rxMethod<{ category: string; year: string; make: string }>(
      pipe(
        switchMap(({ category, year, make }) => {
          const vehicleType = environment.features.shopping.defaultVehicleType || category;
          const programId = localStorage.getItem('programId') || '';

          return httpClient
            .get<ManualResponse>(`${environment.apiUrl}/manualdecode`, {
              params: getParams(year, make, null, vehicleType, programId),
            })
            .pipe(
              tapResponse<ManualResponse>({
                next: (response: ManualResponse) => {
                  patchState(store, {
                    manualDecode: {
                      ...store.manualDecode(),
                      model:
                        !response.model || response.model.length === 0 || (response.model.length === 1 && response.model[0] == '')
                          ? ['OTHER']
                          : [...response.model],
                    },
                    error: '',
                  });
                },
                error: ( error: unknown ) => {
                  console.error(error);
                  const httpError = error as { error?: { status?: number }; status?: number };
                  const errorBody = httpError.error as { status?: number } | undefined;
                  const applicationStatus = errorBody?.status;
                  if (applicationStatus !== 330) {
                    const vinReset = new DecodeVinResponse();
                    vinReset.vin = store.vin().vin;

                    patchState(store, {
                      error: applicationStatus?.toString() || httpError.status?.toString() || '',
                      vin: vinReset,
                      decodeVinTries: store.decodeVinTries() + 1,
                    });
                  }
                },
              })
            );
        })
      )
    );

    const getTrim = rxMethod<{ category: string; year: string; make: string; model: string }>(
      pipe(
        switchMap(({ year, make, model, category }) => {
          const vehicleType = environment.features.shopping.defaultVehicleType || category;
          const programId = localStorage.getItem('programId') || '';

          return httpClient
            .get<ManualResponse>(`${environment.apiUrl}/manualdecode`, {
              params: getParams(year, make, model, vehicleType, programId),
            })
            .pipe(
              tapResponse<ManualResponse>({
                next: (response: ManualResponse) => {
                  const responseRecord = response as ManualResponse & { status?: unknown; code?: unknown };
                  const noRecordsInBody =
                    toStatusNumber(responseRecord.status) === MANUAL_DECODE_NO_RECORDS_STATUS ||
                    toStatusNumber(responseRecord.code) === MANUAL_DECODE_NO_RECORDS_STATUS;
                  const trim = noRecordsInBody || isEmptyTrim(response.trim) ? [] : response.trim;

                  patchState(store, {
                    manualDecode: { ...store.manualDecode(), trim },
                    error: '',
                  });
                },
                error: ( error: unknown ) => {
                  console.error(error);

                  // HTTP 330 with an empty body: error.error is null, so body.status is missing.
                  // The trim request already has year/make/model, so keep the VIN decode.
                  if (year && make && model && (isNoRecordsFoundError(error) || hasDecodedVehicleWithoutTrim(store.vin()))) {
                    patchState(store, {
                      manualDecode: { ...store.manualDecode(), trim: [] },
                      error: '',
                    });
                    return;
                  }

                  if (!isNoRecordsFoundError(error)) {
                    const vinReset = new DecodeVinResponse();
                    vinReset.vin = store.vin().vin;

                    patchState(store, {
                      error: toStatusNumber((error as HttpErrorResponse).status)?.toString() || '',
                      vin: vinReset,
                      decodeVinTries: store.decodeVinTries() + 1,
                    });
                  }
                },
              })
            );
        })
      )
    );

    const getCustomerPostal = rxMethod<{ zipCode: string; values: Record<string, unknown> }>(
      pipe(
        switchMap(({ zipCode }) => {
          return httpClient.post<PostalResponse>(`${environment.apiUrl}/getcitystate`, { postalcode: zipCode }).pipe(
            tapResponse<PostalResponse>({
              next: (postal: PostalResponse) => patchState(store, { postal }),
              error: ( error: unknown ) => {
                const httpError = error as { status?: number };
                console.error(error);
                const vinReset = new DecodeVinResponse();
                vinReset.vin = store.vin().vin;

                patchState(store, {
                  error: httpError.status?.toString() || '',
                  vin: vinReset,
                  decodeVinTries: store.decodeVinTries() + 1,
                });
              },
            })
          );
        })
      )
    );

    function resetProductForm(): void {
      patchState(store, {
        values: {} as VehicleValues,
        decodeVinTries: 0,
        vin: new DecodeVinResponse(),
        postal: new PostalResponse(),
        error: '',
        status: false,
        selectedSurcharges: [],
        manualDecode: new ManualResponse(),
        disableInServiceDate: false,
        disableVehicleCondition: false,
        financeForm: { ...InitialFinanceFormState },
        borrowInfoForm: { ...InitialBorrowInfoFormState },
        chargerForm: initialChargerForm,
        isManualDecodeModeEnabled: false,
        dotInfo: {
          dotForm: [initialDOTForm],
        },
      });
    }

    function resetVinDecode(): void {
      patchState(store, {
        values: {
          ...store.values(),
          vin: null,
          year: null,
          make: null,
          inServiceDate: null,
          model: null,
          trim: null,
        },
        decodeVinTries: 0,
        disableInServiceDate: false,
        error: '',
        vin: new DecodeVinResponse(),
        manualDecode: new ManualResponse(),
      });
    }

    function updateProductForm(values: Partial<VehicleValues>): void {
      patchState(store, {
        values: { ...store.values(), ...values },
      });
    }

    function updateManualDecodeMode(isManualDecodeModeEnabled: boolean): void {
      patchState(store, { isManualDecodeModeEnabled });
    }

    function updateChargerForm(chargerForm: Partial<ChargerForm>): void {
      patchState(store, {
        chargerForm: { ...store.chargerForm(), ...chargerForm },
      });
    }

    function updateDOTForm(dotForm: DOTForm[]): void {
      patchState(store, {
        dotInfo: { dotForm: [...dotForm] },
      });
    }

    function updateBorrowInfoForm(
      DOBBorrower: IComplexDate,
      DOBCoBorrower: IComplexDate,
      coBorrower: Options,
      provinceBorrower: string,
      provinceCoBorrower: string
    ): void {
      patchState(store, {
        borrowInfoForm: {
          ...store.borrowInfoForm(),
          DOBBorrower,
          DOBCoBorrower,
          coBorrower,
          provinceBorrower,
          provinceCoBorrower,
        },
      });
    }

    function toggleChargerForm(value: boolean): void {
      patchState(store, { chargerFormEnabled: value });
    }

    function toggleVehicleForm(value: boolean): void {
      patchState(store, { vehicleFormEnabled: value });
    }

    function updateFinanceForm(values: Partial<FinanceForm>): void {
      patchState(store, { financeForm: { ...store.financeForm(), ...values } });
    }

    function clearCustomerPostal(): void {
      patchState(store, { postal: new PostalResponse() });
    }

    function showInServiceDateAndOdo(): void {
      patchState(store, {
        vin: { ...store.vin(), manualDecode: true },
        error: '',
      });
    }

    function loadVehicleQuote(loadQuote: LoadQuote): void {
      loadQuote.VehicleState.vin.loadQuote = true;

      const useQuoteOnlyEndpoint: boolean = environment.features.useQuoteOnlyEndpoint;
      let chargerForm;
      let dotInfo: { dotForm: DOTForm[] };
      if (useQuoteOnlyEndpoint && loadQuote.VehicleState.chargerForm) {
        chargerForm = {
          serialNumber: loadQuote.VehicleState.chargerForm.serialNumber,
          chargerModel: loadQuote.VehicleState.chargerForm.chargerModel,
          chargerYear: loadQuote.VehicleState.chargerForm.chargerYear,
          chargerPurchaseDate: loadQuote.VehicleState.chargerForm.chargerPurchaseDate,
          chargerPurchasePrice: loadQuote.VehicleState.chargerForm.chargerPurchasePrice,
        };
      } else {
        chargerForm = initialChargerForm;
      }
      if (useQuoteOnlyEndpoint && loadQuote.VehicleState?.dotInfo?.dotForm?.length) {
        const loadedDotForm = loadQuote.VehicleState.dotInfo.dotForm;
        dotInfo = {
          dotForm:
            Array.isArray(loadedDotForm) && loadedDotForm.length > 0 ? [...loadedDotForm] : [initialDOTForm],
        };
      } else if (useQuoteOnlyEndpoint && loadQuote.VehicleState['dotForm']) {
        dotInfo = {
          dotForm: [
            {
              rimSize: loadQuote.VehicleState['dotForm'].rimSize,
              quantity: loadQuote.VehicleState['dotForm'].quantity,
              dot: loadQuote.VehicleState['dotForm'].dot,
            },
            initialDOTForm,
          ],
        };
      } else if (useQuoteOnlyEndpoint && loadQuote.VehicleState['dotForm1']) {
        dotInfo = {
          dotForm: [
            {
              rimSize: loadQuote.VehicleState['dotForm1'].rimSize,
              quantity: loadQuote.VehicleState['dotForm1'].quantity,
              dot: loadQuote.VehicleState['dotForm1'].dot,
            },
          ],
        };
        if (loadQuote.VehicleState['dotForm2']) {
          dotInfo.dotForm.push({
            rimSize: loadQuote.VehicleState['dotForm2'].rimSize,
            quantity: loadQuote.VehicleState['dotForm2'].quantity,
            dot: loadQuote.VehicleState['dotForm2'].dot,
          });
        }
      } else {
        dotInfo = { dotForm: [initialDOTForm] };
      }

      const borrowInfoForm: BorrowInfoForm = loadQuote.VehicleState.borrowInfoForm
        ? {
            DOBBorrower: loadQuote.VehicleState.borrowInfoForm.DOBBorrower,
            DOBCoBorrower: loadQuote.VehicleState.borrowInfoForm.DOBCoBorrower,
            coBorrower: loadQuote.VehicleState.borrowInfoForm.coBorrower,
            provinceBorrower: loadQuote.VehicleState.borrowInfoForm.provinceBorrower,
            provinceCoBorrower: loadQuote.VehicleState.borrowInfoForm.provinceCoBorrower,
          }
        : InitialBorrowInfoFormState;

      const { financeType, financeAmount, apr, loanTerm, monthlyPayment, amortizationPeriod, residualValue } =
        loadQuote.VehicleState.values;

      const financeForm: FinanceForm = {
        financeType,
        financeAmount,
        apr,
        loanTerm,
        monthlyPayment,
        amortizationPeriod,
        residualValue,
      };

      patchState(store, {
        borrowInfoForm,
        values: loadQuote.VehicleState.values,
        status: loadQuote.VehicleState.status,
        vin: loadQuote.VehicleState.vin,
        postal: loadQuote.VehicleState.postal ? loadQuote.VehicleState.postal : new PostalResponse(),
        chargerForm,
        dotInfo,
        vehicleFormEnabled: loadQuote.VehicleState.vehicleFormEnabled,
        financeForm,
      });
    }

    function setVehicleCondition(isOldVehicleCondition: boolean): void {
      patchState(store, { values: { ...store.values(), isOldVehicleCondition } });
    }

    function setCreditStatus(isCreditAvailable: boolean): void {
      patchState(store, { values: { ...store.values(), isCreditAvailable } });
    }

    function setSaveCredit(isValidSaveCredit: boolean): void {
      patchState(store, { values: { ...store.values(), isValidSaveCredit } });
    }

    function setSurcharges(surcharges: { name: string; selected: boolean }[]): void {
      patchState(store, { values: { ...store.values(), surcharges }, selectedSurcharges: surcharges });
    }

    function clearSurcharges(): void {
      patchState(store, {
        values: {
          ...store.values(),
          surcharges: Array.isArray(store.values().surcharges)
            ? store.values().surcharges.map((s) => ({ ...s, selected: false }))
            : [],
        },
        selectedSurcharges: store.selectedSurcharges()?.map((s) => ({ ...s, selected: false })),
      });
    }

    return {
      clearCustomerPostal,
      clearSurcharges,
      decodeVin,
      getCustomerPostal,
      getMake,
      getModel,
      getTrim,
      getYear,
      loadVehicleQuote,
      resetProductForm,
      resetVinDecode,
      setCreditStatus,
      setSaveCredit,
      setSurcharges,
      setVehicleCondition,
      showInServiceDateAndOdo,
      toggleChargerForm,
      toggleVehicleForm,
      updateBorrowInfoForm,
      updateChargerForm,
      updateDOTForm,
      updateFinanceForm,
      updateManualDecodeMode,
      updateProductForm,
    };
  }),
  withHooks({
    onInit(store) {
      const vehicleStoreRequestsDispatcher = inject(VehicleStoreRequestsDispatcher);
      const subscriptions: Subscription[] = [];

      subscriptions.push(
        vehicleStoreRequestsDispatcher.resetProductFormRequested$.subscribe((resetProductForm) => {
          if (resetProductForm) {
            store.resetProductForm();
          }
        })
      );

      return () => {
        subscriptions.forEach((sub) => sub.unsubscribe());
      };
    },
  })
);
